import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LogIn, LogOut, UserCircle } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { useAuth, isStaff } from "../context/AuthContext";
import { fireEvent } from "./StoreButtons";

const NAV = [
    { to: "/", label: "Accueil" },
    { to: "/fonctionnement", label: "Fonctionnement" },
    { to: "/fonctionnalites", label: "Fonctionnalités" },
    { to: "/boutique", label: "Boutique" },
    { to: "/avis", label: "Avis" },
    { to: "/#tarifs", label: "Tarifs", anchor: "tarifs" },
    { to: "/#faq", label: "FAQ", anchor: "faq" },
    { to: "/#contact", label: "Contact", anchor: "contact" },
];

export function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    useLocation();
    const accountPath = isStaff(user) ? "/admin" : "/mon-compte";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleLogout = async () => {
        setOpen(false);
        await logout();
        navigate("/");
    };

    const go = (item) => (e) => {
        if (item.to === "/boutique") fireEvent("boutique_click", "navbar");
        if (item.anchor) {
            e.preventDefault();
            setOpen(false);
            if (window.location.pathname !== "/") {
                navigate("/");
                setTimeout(() => document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth" }), 350);
            } else {
                document.getElementById(item.anchor)?.scrollIntoView({ behavior: "smooth" });
            }
        } else {
            setOpen(false);
        }
    };

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "glass-light border-b border-border shadow-sm" : "bg-ivory/60 backdrop-blur-md"
            }`}
            data-testid="main-navbar"
        >
            <div className="container-bc flex items-center justify-between h-16 sm:h-20">
                <Link to="/" aria-label="BODYCOACHIA — Accueil" data-testid="nav-home-link">
                    <Logo dark={false} />
                </Link>
                <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.label}
                            to={item.to}
                            onClick={go(item)}
                            className={({ isActive }) =>
                                `text-sm font-medium transition-colors ${
                                    isActive && !item.anchor ? "text-forest" : "text-ink-soft hover:text-forest"
                                }`
                            }
                            data-testid={`nav-${item.anchor || item.label.toLowerCase()}-link`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        <>
                            <Link to={accountPath} data-testid="nav-account-link">
                                <Button variant="outline" size="sm" className="border-forest/30 text-forest hover:bg-pearl gap-1.5">
                                    <UserCircle size={15} /> Mon espace
                                </Button>
                            </Link>
                            <button onClick={handleLogout} aria-label="Déconnexion" className="text-ink-soft hover:text-red-600 transition-colors p-2" data-testid="nav-logout-button">
                                <LogOut size={17} />
                            </button>
                        </>
                    ) : (
                        <Link to="/connexion" data-testid="nav-login-button">
                            <Button variant="outline" size="sm" className="border-forest/30 text-forest hover:bg-pearl gap-1.5">
                                <LogIn size={15} /> Connexion
                            </Button>
                        </Link>
                    )}
                    <a href="/#telecharger" onClick={go({ anchor: "telecharger" })} data-testid="nav-download-cta">
                        <Button size="sm" className="bg-forest text-ivory hover:bg-forest-surface font-semibold">
                            Télécharger
                        </Button>
                    </a>
                </div>
                <button className="lg:hidden text-forest p-2" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="nav-mobile-toggle">
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>
            {open && (
                <nav className="lg:hidden glass-light border-t border-border px-6 py-4 flex flex-col gap-4" aria-label="Navigation mobile">
                    {NAV.map((item) => (
                        <NavLink key={item.label} to={item.to} onClick={go(item)} className="text-ink text-base py-1" data-testid={`nav-mobile-${item.anchor || item.label.toLowerCase()}`}>
                            {item.label}
                        </NavLink>
                    ))}
                    {user ? (
                        <>
                            <NavLink to={accountPath} onClick={() => setOpen(false)} className="text-forest font-semibold text-base py-1 flex items-center gap-2" data-testid="nav-mobile-account">
                                <UserCircle size={17} /> Mon espace
                            </NavLink>
                            <button onClick={handleLogout} className="text-left text-ink-soft text-base py-1 flex items-center gap-2" data-testid="nav-mobile-logout">
                                <LogOut size={17} /> Déconnexion
                            </button>
                        </>
                    ) : (
                        <NavLink to="/connexion" onClick={() => setOpen(false)} className="text-forest text-base font-semibold py-1 flex items-center gap-2" data-testid="nav-mobile-login">
                            <LogIn size={17} /> Connexion
                        </NavLink>
                    )}
                </nav>
            )}
        </header>
    );
}
